/**
 * Faixa de preço aceita pelo motorista, em torno do valor sugerido.
 *
 * A regra do produto: o preço pode ficar até 25% ABAIXO e 20% ACIMA do
 * sugerido. Duas regras convivem:
 *
 *  1. **Piso absoluto de R$ 10 para qualquer valor.** É o mesmo número que o
 *     zod exige no servidor (`lib/validations/ride.ts`, `price` e `stops[].price`
 *     com `.min(10)`). Se a faixa relativa mandasse, um trecho curto cairia
 *     abaixo de 10 e o backend rejeitaria a carona inteira — a regra do produto
 *     viraria erro de validação.
 *  2. **Acima disso, a faixa é relativa ao sugerido.** Sugerido 100 → aceita
 *     de 75 a 120.
 *
 * Isso é exatamente `max(10, round(sugerido * 0.75))` no piso. O `10` é o
 * PISO, não o valor: foi por confundir os dois que `/offer-seats` passava
 * `minPrice={10}` para o seletor, o que matava a regra dos -25% por completo
 * (o `minPriceProp ?? ...` dava vitória ao override) e fazia o teto de +20%
 * continuar valendo sozinho. Pista de uma regra que some em metade dos casos
 * sem erro nenhum.
 *
 * Módulo PURO e separado de propósito: o jest deste repo roda em
 * `testEnvironment: node` e o `testMatch` só pega arquivos `.test.ts` dentro de
 * `__tests__`, ou seja, componente React não é testável aqui. Se a regra morar
 * dentro do JSX, ela não tem como ser testada — e aí volta a depender de alguém
 * lembrar.
 */

/** Piso absoluto, em reais. Espelha `.min(10)` do zod em `lib/validations/ride.ts`. */
export const PRICE_ABSOLUTE_FLOOR = 10

/** Fração inferior aceita, em relação ao sugerido (−25%). */
export const PRICE_BAND_LOW = 0.75

/** Fração superior aceita, em relação ao sugerido (+20%). */
export const PRICE_BAND_HIGH = 1.2

export interface PriceBounds {
  min: number
  max: number
}

/**
 * Faixa aceita para um sugerido.
 *
 * Sugerido ausente, zero, negativo ou não-finite cai no piso absoluto com teto
 * proporcional: é o estado de `/offer-seats` antes de a rota responder, quando
 * `suggestedStopPrice` devolve o piso e o trecho ainda não tem legs.
 */
export function priceBounds(suggested: number): PriceBounds {
  const base = Number.isFinite(suggested) && suggested > 0 ? suggested : PRICE_ABSOLUTE_FLOOR
  const max = Math.max(PRICE_ABSOLUTE_FLOOR, Math.round(base * PRICE_BAND_HIGH))
  const min = Math.min(max, Math.max(PRICE_ABSOLUTE_FLOOR, Math.round(base * PRICE_BAND_LOW)))
  return { min, max }
}

/**
 * Traz `value` para dentro da faixa.
 *
 * Tolera dado sujo de propósito: `stop.price` nasce `0` (estado "ainda não
 * escolhido", não "R$ 0"), e um `NaN` aqui viraria `NaN` no payload e reprovaria
 * a carona no zod.
 *
 * Não-finite resolve pelo SINAL, não tudo para o mesmo lado: `Infinity` vai para
 * o TETO e `-Infinity`/`NaN` vão para o PISO. Mandar `Infinity` para o piso faz
 * um teto de +20% virar redução de 92% — é o mesmo tipo de erro silencioso que
 * esta função existe para matar, só que camuflado de sanitização.
 */
export function clampToBounds(value: number, bounds: PriceBounds): number {
  const { min, max } = bounds
  if (!Number.isFinite(value)) return value === Infinity ? max : min
  return Math.min(max, Math.max(min, Math.round(value)))
}

/** `true` quando o valor está dentro da faixa — para aviso em UI, não para corrigir. */
export function isWithinBounds(value: number, bounds: PriceBounds): boolean {
  return Number.isFinite(value) && value >= bounds.min && value <= bounds.max
}
